import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import { TRY_GROUP_ISSUES, TriageViewType, tryGroupIssues } from '@/actions/triage';
import { AutomationType, IssueEntity } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAutomationsByType, selectIsAutomationEnabled } from '@/selectors/selectors';
import { runwayColors, rwColors } from '@/components/styles/globalStyles';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import AutomationSettingsBanner from '@/components/sharedComponents/AutomationSettingsBanner';
import { FixRequestFormValues, useFixRequestForm } from './FixRequestForm';

interface GroupRelatedModalProps {
    open: boolean;
    onClose: () => void;
    onGrouped: () => void;
    orgId: string;
    appId: string;
    selectedIssues: IssueEntity[];
    viewType: TriageViewType;
    // When true, the combined issue is escalated to a fix request after merging
    escalate?: boolean;
}

interface GroupRelatedFormValues extends FixRequestFormValues {
    title?: string;
    description?: string;
}

export const GroupRelatedModal: React.FC<GroupRelatedModalProps> = ({
    open,
    onClose,
    onGrouped,
    orgId,
    appId,
    selectedIssues,
    viewType,
    escalate,
}) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm<GroupRelatedFormValues>();

    // Same app-level automation as the request-fix flow (escalate creates a fix request via the same path): when it
    // will auto-create a PM ticket, the work item is optional.
    const createTicketAutomationId = useSelector(
        (state: RootState) =>
            selectAutomationsByType(state, appId, AutomationType.AutoCreatePMTicketOnTriageIssueFixRequest)?.[0]?.id,
    );
    const isAutoCreateTicketEnabled = useSelector((state: RootState) =>
        selectIsAutomationEnabled(state, appId, createTicketAutomationId),
    );

    const { nextVersion, selectedVersion, fields, seedOnOpen, buildFormFieldsValues, buildWorkItems, onOpenChange } =
        useFixRequestForm({ appId, orgId, form, workItemOptional: isAutoCreateTicketEnabled });

    // The combined issue must be the one with a fix request (if any) so that fix request is preserved; the others
    // are merged into it and deleted. Otherwise just keep the first selected.
    const target = selectedIssues.find((issue) => !!issue.fixRequest) ?? selectedIssues[0];
    const targetId = target?.id ?? '';
    const isMergeIntoFixRequest = !!target?.fixRequest;

    const isSaving = useIsLoading(TRY_GROUP_ISSUES, targetId);
    const saveError = useError(TRY_GROUP_ISSUES, targetId);
    const saveSucceeded = useUpdated(TRY_GROUP_ISSUES, targetId);

    React.useEffect(() => {
        if (saveSucceeded) {
            form.resetFields();
            onClose();
            onGrouped();
        }
    }, [saveSucceeded]);

    const onSubmit = (values: GroupRelatedFormValues) => {
        if (!target) {
            return;
        }
        dispatch(
            tryGroupIssues({
                orgId,
                appId,
                targetIssueId: target.id,
                sourceIssueIds: selectedIssues.filter((issue) => issue.id !== target.id).map((issue) => issue.id),
                title: values.title,
                description: values.description,
                ...(escalate ?
                    {
                        version: values.version ?? nextVersion,
                        formFieldsValues: buildFormFieldsValues(values),
                        workItems: buildWorkItems(),
                        viewType,
                    }
                :   {}),
            }),
        );
    };

    const modalTitle =
        escalate ? 'Group and escalate to fix request'
        : isMergeIntoFixRequest ? 'Merge into existing issue with fix request'
        : 'Group related issues';
    const okText =
        escalate ? 'Group and escalate'
        : isMergeIntoFixRequest ? 'Merge'
        : 'Group';
    const okDisabled = escalate && !selectedVersion;

    return (
        <Modal
            title={modalTitle}
            open={open}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText={okText}
            okButtonProps={{
                disabled: okDisabled,
                style: {
                    backgroundColor: rwColors.primary,
                    borderColor: rwColors.primary,
                    color: runwayColors.white,
                    opacity: okDisabled ? 0.5 : undefined,
                },
            }}
            confirmLoading={isSaving}
            afterOpenChange={(isOpen) => {
                onOpenChange(isOpen);
                if (isOpen && escalate) {
                    seedOnOpen();
                }
            }}
            destroyOnClose
            width={600}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                requiredMark
                preserve={false}
                style={{ marginTop: 12 }}
            >
                {saveError && <Alert type="error" message={saveError} showIcon style={{ marginBottom: 16 }} />}
                <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
                    <Input placeholder="Title for the combined issue" />
                </Form.Item>
                <Form.Item name="description" label="Description">
                    <Input.TextArea
                        placeholder="Description for the combined issue"
                        autoSize={{ minRows: 3, maxRows: 8 }}
                    />
                </Form.Item>
                {escalate && fields}
            </Form>
            {escalate && createTicketAutomationId && (
                <AutomationSettingsBanner
                    automationId={createTicketAutomationId}
                    appId={appId}
                    orgId={orgId}
                    isEnabled={isAutoCreateTicketEnabled}
                    bannerStyle={{ marginTop: 15, padding: 12 }}
                    bannerText={
                        isAutoCreateTicketEnabled ?
                            <Text style={{ flexGrow: 1, marginRight: 15 }}>
                                &quot;Create a PM ticket on triage fix request&quot; (automation) is <b>enabled</b>. A
                                ticket will be created and associated automatically unless you associate one here — so a
                                work item is optional.
                            </Text>
                        :   <Text type="secondary" style={{ flexGrow: 1, marginRight: 15 }}>
                                &quot;Create a PM ticket on triage fix request&quot; (automation) is <b>disabled</b>.
                                You must associate at least one work item.
                            </Text>
                    }
                />
            )}
        </Modal>
    );
};
